import { yupResolver } from '@hookform/resolvers/yup';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { FieldPath, useFieldArray, useForm } from 'react-hook-form';

import { DATE_FORMAT } from 'coreOld/constants';
import { ApiStatuses, NotificationPosition } from 'coreOld/enums';
import { useNotification } from 'coreOld/hooks/useNotification';
import { formatDate, isObject } from 'coreOld/utils';
import { FormType } from 'partsOld/enums';
import { useCheckActiveOverlappingUpsert, usePricingActions } from 'partsOld/hooks';
import { getActiveOverlappingPrice } from 'partsOld/services/pricing';
import { ICompanyPart, IPricing, IPricingItem, IPricingForm } from 'partsOld/types';

import PricingFormView from './PricingFormView';
import { pricingFormSchema } from './pricingFormSchema';

interface IProps {
  setIsFormOpen: Dispatch<SetStateAction<boolean>>;
  isFormOpen: boolean;
  part?: Partial<ICompanyPart>;
  selectedPrice?: IPricing;
  setSelectedPrice?: Dispatch<SetStateAction<IPricing>>;
}

const PricingForm = ({
  setIsFormOpen,
  isFormOpen,
  selectedPrice,
  setSelectedPrice,
  part,
}: IProps) => {
  const isPriceSelected = Boolean(selectedPrice);

  const [removedItems, setRemovedItems] = useState([]);

  const { upsertPrice, removePrice } = usePricingActions();
  const {
    onCancel,
    onSubmitCheck,
    onRemove,
    onCancelRemove,
    onCancelBtnClick,
    removedItem,
    isActiveItemOverlapping,
  } = useCheckActiveOverlappingUpsert<IPricing>(
    FormType.PRICE,
    selectedPrice,
    setSelectedPrice,
    upsertPrice,
    removePrice,
    getActiveOverlappingPrice
  );

  const { showNotification } = useNotification();

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
    getValues,
    watch,
    trigger: triggerValidation,
  } = useForm<IPricingForm>({
    resolver: yupResolver(pricingFormSchema),
    defaultValues: selectedPrice,
  });

  const { fields, append, remove } = useFieldArray({
    name: 'priceItems',
    keyName: 'fieldId',
    control,
  });

  useEffect(() => {
    if (fields.length < 1 && isFormOpen) {
      addPrice(true);
    }
  }, [isFormOpen]);

  useEffect(() => {
    if (isObject(selectedPrice)) {
      reset(selectedPrice);
    }
  }, [selectedPrice]);

  useEffect(() => {
    if (part) {
      reset({
        startDate: new Date(),
        uom: part?.uom,
        priceItems: [
          {
            minQuantity: part?.orderIncrement || 1,
          },
        ],
      });
    }
  }, [isFormOpen]);

  const addPrice = async (skipValidation = false) => {
    if (skipValidation) {
      append({
        maxQuantity: null,
        minQuantity: null,
        discountIncrease: null,
        marginReduction: null,
      });

      return;
    }

    const isValid = await triggerValidation();

    if (isValid) {
      append({
        maxQuantity: null,
        minQuantity: null,
        discountIncrease: null,
        marginReduction: null,
      });
    }
  };

  const removePriceItem = (index: number, item?: IPricingItem) => {
    if (item.id) {
      removedItems.push(item.id);
    }

    remove(index);
  };

  const onCloseModal = () => {
    reset({ uom: null });

    setRemovedItems([]);
    setIsFormOpen(false);
    setSelectedPrice(null);
  };

  const onChangeDate = (date, field: FieldPath<IPricingForm>) => {
    setValue(field, date);
  };

  const onSubmit =
    (skipActiveCheck = false) =>
    async (data) => {
      const dataToSend = {
        startDate: formatDate(data?.startDate, true, DATE_FORMAT),
        endDate: formatDate(data?.endDate, true, DATE_FORMAT),
        id: data.id || '',
        priceItems: data.priceItems,
        corporatePartId: part?.id,
        part: part?.part,
        manufacturerId: part?.manufacturerId,
        removedItems,
      };

      onSubmitCheck(dataToSend, skipActiveCheck, onCloseModal);
    };

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const error = errors?.priceItems?.pop()?.maxQuantity.message;

      showNotification({
        message: error,
        type: ApiStatuses.ERROR,
        position: NotificationPosition.MODAL,
      });

      return;
    }

    showNotification(null);
  }, [errors]);

  return (
    <PricingFormView
      control={control}
      handleSubmit={handleSubmit}
      getValues={getValues}
      onSubmit={onSubmit}
      startDate={watch('startDate') ? new Date(watch('startDate')) : new Date()}
      endDate={watch('endDate') && new Date(watch('endDate'))}
      onChangeDate={onChangeDate as (date: any, field: string) => void}
      addPrice={addPrice}
      removePrice={removePriceItem}
      priceItems={fields}
      isFormOpen={isFormOpen || Boolean(selectedPrice)}
      onCloseModal={onCloseModal}
      isPriceSelected={isPriceSelected}
      isActivePriceOverlapping={isActiveItemOverlapping}
      onCancel={onCancel}
      onRemove={onRemove}
      onCancelRemove={onCancelRemove}
      removedItem={removedItem}
      onCancelBtnClick={onCancelBtnClick}
      setValue={setValue}
    />
  );
};

export default PricingForm;
